import { useQuery } from "@apollo/client";
import {
  Alert, Box, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Icon, MenuItem, Pagination, TextField, Typography, useMediaQuery,
} from "@mui/material";
import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import SoftButton from "components/SoftButton";
import { PRODUCTOS_CONFIGURACION_EVENTO } from "services/configuracionEventos";
import { formatoPesos, mensajeErrorEvento } from "../utils";

const POR_PAGINA = 12;

// Permite marcar varios productos y agregarlos de una vez. Los que ya están en
// el evento se muestran marcados y deshabilitados.
export default function SelectorProductosEvento({ open, categorias, idsExistentes, onCancel, onConfirm }) {
  const fullScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const [busqueda, setBusqueda] = useState("");
  const [busquedaAplicada, setBusquedaAplicada] = useState("");
  const [idCategoria, setIdCategoria] = useState("");
  const [pagina, setPagina] = useState(1);
  const [seleccion, setSeleccion] = useState([]);

  useEffect(() => {
    if (!open) return;
    setBusqueda(""); setBusquedaAplicada(""); setIdCategoria(""); setPagina(1); setSeleccion([]);
  }, [open]);

  useEffect(() => {
    const id = setTimeout(() => { setBusquedaAplicada(busqueda.trim()); setPagina(1); }, 300);
    return () => clearTimeout(id);
  }, [busqueda]);

  const filter = useMemo(() => ({
    esIngrediente: false,
    disponibilidad: true,
    ...(idCategoria ? { idCategoria: Number(idCategoria) } : {}),
  }), [idCategoria]);

  const { data, loading, error } = useQuery(PRODUCTOS_CONFIGURACION_EVENTO, {
    variables: { filter, search: busquedaAplicada || null, page: pagina, limit: POR_PAGINA },
    skip: !open,
    fetchPolicy: "network-only",
  });
  const productos = data?.productos || [];
  const paginas = Number(data?.paginationInfo?.pages || 0);
  const existentes = new Set(idsExistentes.map(String));
  const seleccionados = new Set(seleccion.map((producto) => String(producto.id)));

  const alternar = (producto) => setSeleccion((prev) => (
    seleccionados.has(String(producto.id))
      ? prev.filter((item) => String(item.id) !== String(producto.id))
      : [...prev, producto]
  ));

  return (
    <Dialog open={open} onClose={onCancel} fullScreen={fullScreen} fullWidth maxWidth="lg" aria-labelledby="selector-productos-evento">
      <DialogTitle id="selector-productos-evento">Agregar productos al evento</DialogTitle>
      <DialogContent dividers sx={{ display: "grid", gap: 2, gridTemplateColumns: "minmax(0, 1fr)", alignContent: "start", overflowX: "hidden", px: { xs: 2, sm: 3 } }}>
        <Typography variant="body2" color="text.secondary">
          Marque todos los productos que quiera vender y presione «Agregar». No se muestran ingredientes ni productos sin disponibilidad.
        </Typography>
        <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "minmax(0, 2fr) minmax(0, 1fr)" } }}>
          <TextField size="small" placeholder="Buscar por nombre" value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)} inputProps={{ "aria-label": "Buscar producto" }} />
          <TextField size="small" select value={idCategoria} onChange={(e) => { setIdCategoria(e.target.value); setPagina(1); }}
            SelectProps={{ displayEmpty: true }} inputProps={{ "aria-label": "Categoría" }}>
            <MenuItem value="">Todas las categorías</MenuItem>
            {categorias.map((categoria) => <MenuItem key={categoria.id} value={categoria.id}>{categoria.categoria}</MenuItem>)}
          </TextField>
        </Box>
        {error && <Alert severity="error">No se pudieron cargar los productos: {mensajeErrorEvento(error)}</Alert>}
        {loading ? <Box display="grid" sx={{ placeItems: "center", minHeight: 200 }}><CircularProgress size={28} /></Box> : (
          <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(3, minmax(0, 1fr))" } }}>
            {productos.map((producto) => {
              const yaEsta = existentes.has(String(producto.id));
              const marcado = yaEsta || seleccionados.has(String(producto.id));
              return (
                <Box key={producto.id} component="label" sx={{
                  display: "flex", alignItems: "center", gap: 1, p: 1, minWidth: 0, borderRadius: 1.5, cursor: yaEsta ? "default" : "pointer",
                  border: "1px solid", borderColor: marcado ? "#67C1B8" : "#E2E8F0", bgcolor: marcado ? "rgba(103, 193, 184, 0.08)" : "#fff", opacity: yaEsta ? 0.6 : 1,
                }}>
                  <Checkbox checked={marcado} disabled={yaEsta} onChange={() => alternar(producto)}
                    inputProps={{ "aria-label": `Seleccionar ${producto.nombre}` }} />
                  <Box minWidth={0} flex={1}>
                    <Typography variant="body2" fontWeight={600} noWrap title={producto.nombre}>{producto.nombre}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {producto.categoriaProducto?.categoria || "Sin categoría"} · Carta {formatoPesos(producto.precioCarta)}
                    </Typography>
                  </Box>
                  {yaEsta && <Chip size="small" label="Ya agregado" />}
                </Box>
              );
            })}
            {!productos.length && <Typography variant="body2" color="text.secondary">No hay productos que coincidan.</Typography>}
          </Box>
        )}
        {paginas > 1 && <Box display="flex" justifyContent="center"><Pagination count={paginas} page={pagina} onChange={(_, value) => setPagina(value)} size="small" /></Box>}
      </DialogContent>
      <DialogActions>
        <Typography variant="caption" sx={{ mr: "auto", pl: 1 }}>{seleccion.length} seleccionado(s)</Typography>
        <SoftButton variant="outlined" color="dark" circular onClick={onCancel}>Cancelar</SoftButton>
        <SoftButton color="primary" circular disabled={!seleccion.length} onClick={() => onConfirm(seleccion)}>
          <Icon>add</Icon>&nbsp;Agregar
        </SoftButton>
      </DialogActions>
    </Dialog>
  );
}

SelectorProductosEvento.propTypes = {
  open: PropTypes.bool.isRequired,
  categorias: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), categoria: PropTypes.string })).isRequired,
  idsExistentes: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};
